<template>
    <v-card class="round-card background-2 mb-4">
        <v-card-title class="pa-3">
            <v-icon color="success" class="mr-3">mdi-wrench</v-icon>
            <p>Nuovo Intervento</p>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text>
            <div v-if="loading" class="text-center py-4">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <div v-else>
                <!-- Toggle Invio Email -->
                <v-switch
                    v-model="sendEmail"
                    label="Invia email al cliente"
                    color="primary"
                    class="mt-0 px-6"
                    inset
                    :disabled="!interventionTemplate"
                ></v-switch>

                <!-- Alert template mancante -->
                <v-alert
                    v-if="!interventionTemplate"
                    type="error"
                    dense
                    outlined
                    class="mb-3 mx-6"
                >
                    Nessun template configurato per l'invio email intervento.
                    Configura un template nelle impostazioni Helpdesk.
                </v-alert>

                <!-- Info template (se configurato) -->
                <v-alert
                    v-else-if="sendEmail"
                    type="info"
                    dense
                    outlined
                    class="mb-3 mx-6"
                >
                    <v-icon left small>mdi-file-document-outline</v-icon>
                    Email con template: <strong>{{ interventionTemplate.name }}</strong>
                </v-alert>

                <!-- Sezione Email (visibile solo se sendEmail) -->
                <v-expand-transition>
                    <div v-if="sendEmail && interventionTemplate" class="px-6">
                        <!-- Destinatario -->
                        <v-combobox
                            v-model="emailForm.to"
                            :items="emailsTo"
                            label="A"
                            v-validate="sendEmail ? 'email|required' : ''"
                            :data-vv-name="'A'"
                            :error-messages="errors.collect('A')"
                            outlined
                            dense
                            class="mb-0"
                            :return-object="false"
                        >
                            <template v-slot:item="data">
                                <v-list-item-avatar size="32px" class="primary mr-4">
                                    <v-icon color="white" small>mdi-email</v-icon>
                                </v-list-item-avatar>
                                <v-list-item-content>
                                    <v-list-item-title>{{ data.item.text || data.item }}</v-list-item-title>
                                </v-list-item-content>
                            </template>
                        </v-combobox>

                        <!-- CC -->
                        <v-combobox
                            v-model="emailForm.cc"
                            label="CC (opzionale)"
                            multiple
                            chips
                            small-chips
                            deletable-chips
                            outlined
                            dense
                            class="mb-3"
                            hint="Premi invio per aggiungere un indirizzo"
                            persistent-hint
                        >
                            <template v-slot:selection="{ attrs, item, parent, selected }">
                                <v-chip
                                    v-bind="attrs"
                                    :input-value="selected"
                                    small
                                    close
                                    @click:close="parent.selectItem(item)"
                                >
                                    {{ item }}
                                </v-chip>
                            </template>
                        </v-combobox>
                    </div>
                </v-expand-transition>

                <v-divider class="my-4"></v-divider>

                <!-- Campi Intervento -->
                <fields-generator
                    v-if="fieldsset"
                    :fieldsset.sync="fieldsset"
                    :model.sync="model"
                    :selectChange="onFieldChange"
                    :support="support"
                    :force-tinymce="true"
                    ref="fieldsgenerator"
                ></fields-generator>
            </div>
        </v-card-text>

        <!-- Allegati -->
        <v-expand-transition>
            <div v-if="showAttachments && sendEmail && fileUploaderInfo.attachableType" class="px-6 pb-4">
                <file-uploader
                    class="pa-0"
                    :id="ticket.id"
                    :attachable-type="fileUploaderInfo.attachableType"
                    :options="fileUploaderInfo.options || {}"
                    :field="fileUploaderInfo"
                    :start-empty="true"
                ></file-uploader>
            </div>
        </v-expand-transition>

        <v-divider></v-divider>

        <v-card-actions class="pa-3">
            <v-btn
                v-if="sendEmail"
                text
                @click="showAttachments = !showAttachments"
                :color="showAttachments ? 'primary' : 'grey'"
            >
                <v-icon left>mdi-paperclip</v-icon>
                Allegati
                <v-icon right small>{{ showAttachments ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn text @click="$emit('cancel')" :disabled="saving">
                Annulla
            </v-btn>
            <v-btn
                color="success"
                @click="save"
                :loading="saving"
            >
                <v-icon left>mdi-content-save</v-icon>
                {{ sendEmail ? 'Salva e Invia' : 'Salva Intervento' }}
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import Vue from 'vue'
import FieldsGenerator from '@/components/common/FieldsGenerator'
import FileUploader from '@/components/common/FileUploader.vue'

export default {
    name: 'InterventionPanel',

    components: {
        FieldsGenerator,
        FileUploader
    },

    props: {
        ticket: {
            type: Object,
            required: true
        },
        defaultStartDate: {
            type: String,
            default: null
        },
        defaultStartTime: {
            type: String,
            default: null
        }
    },

    data() {
        return {
            loading: false,
            saving: false,
            fieldsset: null,
            model: {},
            support: {},
            // Eredita send_mails dal ticket (default true se non definito)
            sendEmail: this.ticket.send_mails !== false,
            emailForm: {
                to: '',
                cc: []
            },
            emailsTo: [],
            fileUploaderInfo: {},
            attachmentIds: [],
            showAttachments: false,
            interventionTemplate: null
        }
    },

    methods: {
        loadFields() {
            this.loading = true
            this.axios.get(`helpdeskinterventions/create?trouble_id=${this.ticket.id}`)
                .then(response => {
                    this.fieldsset = response.data.fields

                    // Set default values from backend fields
                    _.forEach(this.fieldsset.fields, (value) => {
                        if (value.type === 'file') {
                            Vue.set(this.model, value.key, '')
                        }
                        if (value.type === 'checkbox') {
                            Vue.set(this.model, value.key, false)
                        }
                        if (value.default) {
                            Vue.set(this.model, value.key, value.default)
                        }
                    })

                    // Imposta start_date e start_time con l'orario di apertura pagina ticket
                    if (this.defaultStartDate) {
                        Vue.set(this.model, 'start_date', this.defaultStartDate)
                    }
                    if (this.defaultStartTime) {
                        Vue.set(this.model, 'start_time', this.defaultStartTime)
                    }

                    // Info per email
                    const sendMailInfo = response.data.send_mail_info || {}
                    this.fileUploaderInfo = sendMailInfo.file_uploader_info || {}
                    this.emailsTo = sendMailInfo.emails_to || []

                    if (sendMailInfo.default_to) {
                        this.emailForm.to = sendMailInfo.default_to
                    }

                    // Template intervento dalle impostazioni
                    this.interventionTemplate = response.data.intervention_template || null

                    // Disabilita sendEmail se non c'è template o se il ticket ha send_mails false
                    if (!this.interventionTemplate || this.ticket.send_mails === false) {
                        this.sendEmail = false
                    }
                })
                .catch(error => {
                    console.error('Error loading intervention fields:', error)
                    this.$store.commit('setSnackbarError')
                    this.$emit('cancel')
                })
                .finally(() => {
                    this.loading = false
                })
        },

        async save() {
            // Valida campi intervento
            const result = await this.$refs.fieldsgenerator.$validator.validateAll()
            if (!result) return

            // Valida campi email se invio attivo
            if (this.sendEmail) {
                const emailValid = await this.$validator.validateAll()
                if (!emailValid) return
            }

            this.saving = true

            const payload = {
                ...this.model,
                timecardable_id: this.ticket.id,
                send_email: this.sendEmail
            }

            if (this.sendEmail) {
                payload.email_to = this.emailForm.to
                payload.email_cc = this.emailForm.cc
                payload.attachment_ids = this.attachmentIds
            }

            this.axios.post('helpdeskinterventions', payload)
                .then(response => {
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: this.sendEmail ? 'Intervento creato e email inviata' : 'Intervento creato',
                        color: 'success',
                        timeout: 3000
                    })
                    this.$emit('saved', response.data.data)
                    this.reset()
                })
                .catch(error => {
                    console.error('Error saving intervention:', error)
                    this.$store.commit('setSnackbarError')
                })
                .finally(() => {
                    this.saving = false
                })
        },

        onFieldChange(field, event) {
            if (this.$eventBusUtility && this.$eventBusUtility.fieldsGeneratorConditions) {
                Promise.all(this.$eventBusUtility.fieldsGeneratorConditions(
                    field, event, this.fieldsset, this.model, this.support
                )).then(responses => {
                    responses.forEach(response => {
                        if (this.model[response.key] && this.$refs.fieldsgenerator) {
                            const ref = this.$refs.fieldsgenerator.$refs[response.key]
                            if (ref && ref[0]) {
                                ref[0].$emit('change', this.model[response.key])
                            }
                        }
                    })
                })
            }
        },

        reset() {
            this.model = {}
            this.emailForm = { to: '', cc: [] }
            // Reset sendEmail considerando sia il template che il flag del ticket
            this.sendEmail = this.interventionTemplate && this.ticket.send_mails !== false
            this.attachmentIds = []
            this.showAttachments = false
        }
    },

    mounted() {
        this.$eventBusUtility.$on('paths', (files) => {
            this.attachmentIds = files
        })
        this.loadFields()
    },

    beforeDestroy() {
        this.$eventBusUtility.$off('paths')
    }
}
</script>
